import React from 'react'
import { ChatToolCard } from '@/components'
import type { ToolCardStatus, ToolConfigEntry } from '@/components'
import { MESSAGE_STATUS } from '../../contexts/SSEEvents'
import type { MessageStatus } from '../api'

// Loose config shape: the runtime payload from the AI server is a nested object
// whose exact schema we don't statically own here. We only read the fields the
// new tool card surfaces (mode / optimize_for / max_combinations / param count).
interface OptimizerConfigShape {
  optimization_config?: {
    strategy_params?: Record<string, unknown>
    risk_management?: Record<string, unknown>
    mode?: string
    optimize_for?: string
    max_combinations?: number
  }
}

interface OptimizerToolData {
  toolName: string
  toolInput: {
    instructions?: string
  }
  startedAt: string
  completedAt?: string
  status: 'running' | 'completed'
  toolStatus?: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  config?: Record<string, any>
  optid?: string
  error?: string
}

interface BotMessageToolOptimizerProps {
  toolData: OptimizerToolData
  status?: MessageStatus
}

// Live stage labels (only shown while running). Driven by real SSE toolStatus
// updates, not synthetic cycling.
const RUNNING_STAGES: Record<string, string> = {
  OPTIMIZER_STARTED: 'Starting optimizer',
  OPTIMIZER_PREPARING: 'Preparing optimization',
  OPTIMIZER_CONFIGURATION: 'Generating parameter ranges',
  OPTIMIZER_QUEUED: 'Optimizer queued',
  OPTIMIZER_RUNNING: 'Running parameter optimization',
}

export default function BotMessageToolOptimizer({
  toolData,
  status,
}: BotMessageToolOptimizerProps) {
  const cardStatus = resolveStatus(status, toolData.status, toolData.toolStatus)
  const elapsedTime = useElapsedTime(toolData.startedAt, toolData.completedAt, cardStatus === 'running')

  const stage = cardStatus === 'running' ? RUNNING_STAGES[toolData.toolStatus ?? ''] : undefined
  const config = buildConfig(toolData.config)

  return (
    <ChatToolCard
      message={{
        id: 0,
        type: 'tool',
        label: labelFor('Running Optimizer', cardStatus),
        status: cardStatus,
        stage,
        duration: cardStatus === 'done' ? formatTime(elapsedTime) : undefined,
        config,
      }}
    />
  )
}

function labelFor(base: string, status: ToolCardStatus) {
  if (status === 'canceled') return `${base} (Canceled)`
  if (status === 'error') return `${base} (Failed)`
  return base
}

function resolveStatus(
  status: MessageStatus | undefined,
  toolStatus: OptimizerToolData['status'],
  serverStatus: string | undefined,
): ToolCardStatus {
  if (serverStatus === 'OPTIMIZER_CANCELED' || status === MESSAGE_STATUS.CANCELED) return 'canceled'
  if (
    status === MESSAGE_STATUS.FAILED ||
    serverStatus === 'OPTIMIZER_ERROR' ||
    serverStatus === 'OPTIMIZER_TIMEOUT'
  ) {
    return 'error'
  }
  if (
    status === MESSAGE_STATUS.PROCESSED ||
    toolStatus === 'completed' ||
    serverStatus === 'OPTIMIZER_COMPLETED'
  ) {
    return 'done'
  }
  return 'running'
}

function buildConfig(
  config: OptimizerToolData['config'],
): ToolConfigEntry[] | undefined {
  const oc = (config as OptimizerConfigShape | undefined)?.optimization_config
  if (!oc) return undefined
  const paramCount =
    Object.keys(oc.strategy_params ?? {}).length + Object.keys(oc.risk_management ?? {}).length

  const entries: ToolConfigEntry[] = [
    { label: 'Mode', value: oc.mode ?? '' },
    { label: 'Optimize For', value: oc.optimize_for ?? '' },
  ]
  if (oc.max_combinations != null) {
    entries.push({ label: 'Max Combinations', value: String(oc.max_combinations) })
  }
  entries.push({ label: 'Parameters', value: String(paramCount) })
  return entries
}

function useElapsedTime(startedAt: string | undefined, completedAt: string | undefined, isRunning: boolean) {
  const [tickElapsed, setTickElapsed] = React.useState(0)

  React.useEffect(() => {
    if (!startedAt || !isRunning) return
    const start = new Date(startedAt).getTime()
    const interval = setInterval(() => {
      setTickElapsed((Date.now() - start) / 1000)
    }, 100)
    return () => clearInterval(interval)
  }, [startedAt, isRunning])

  if (!isRunning && startedAt && completedAt) {
    const start = new Date(startedAt).getTime()
    const end = new Date(completedAt).getTime()
    return (end - start) / 1000
  }
  return tickElapsed
}

function formatTime(seconds: number) {
  return `${seconds.toFixed(1)}s`
}
